import { z } from 'zod';
import { LIMITS } from "./constants.js";
const snowflake = z.string().min(1).max(32);
/** Nomes de canal seguem o padrão do Discord: minúsculo, sem espaço. */
export const channelNameSchema = z
    .string()
    .trim()
    .min(LIMITS.channelName.min)
    .max(LIMITS.channelName.max)
    .transform((v) => v
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, ''))
    .refine((v) => v.length > 0, { message: 'Nome de canal inválido' });
export const usernameSchema = z
    .string()
    .trim()
    .min(LIMITS.username.min, `Mínimo de ${LIMITS.username.min} caracteres`)
    .max(LIMITS.username.max, `Máximo de ${LIMITS.username.max} caracteres`)
    .regex(/^[a-z0-9._-]+$/, 'Use apenas letras minúsculas, números, ponto, hífen ou underline');
export const passwordSchema = z
    .string()
    .min(LIMITS.password.min, `A senha precisa de ao menos ${LIMITS.password.min} caracteres`)
    .max(LIMITS.password.max);
// ---------------------------------------------------------------------------
// Autenticação
// ---------------------------------------------------------------------------
export const registerSchema = z.object({
    username: usernameSchema,
    displayName: z.string().trim().min(LIMITS.displayName.min).max(LIMITS.displayName.max),
    email: z.email('E-mail inválido').max(255),
    password: passwordSchema,
    inviteCode: z.string().trim().min(1).max(32).optional(),
});
export const loginSchema = z.object({
    /** Aceita e-mail ou nome de usuário no mesmo campo. */
    identifier: z.string().trim().min(1).max(255),
    password: z.string().min(1).max(LIMITS.password.max),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
export const updateSelfSchema = z.object({
    displayName: z.string().trim().min(LIMITS.displayName.min).max(LIMITS.displayName.max).optional(),
    customStatus: z.string().trim().max(LIMITS.customStatus.max).nullable().optional(),
    accentColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
// ---------------------------------------------------------------------------
// Servidores (guilds)
// ---------------------------------------------------------------------------
export const createGuildSchema = z.object({
    name: z.string().trim().min(LIMITS.guildName.min).max(LIMITS.guildName.max),
    description: z.string().trim().max(LIMITS.description.max).optional(),
    /** Cria o par padrão de canais "geral" (texto) e "Sala 1" (voz). */
    template: z.enum(['default', 'empty']).default('default'),
});
export const updateGuildSchema = z.object({
    name: z.string().trim().min(LIMITS.guildName.min).max(LIMITS.guildName.max).optional(),
    description: z.string().trim().max(LIMITS.description.max).nullable().optional(),
    iconUrl: z.string().max(512).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Canais e categorias
// ---------------------------------------------------------------------------
export const createChannelSchema = z.object({
    name: channelNameSchema,
    type: z.enum(['text', 'voice']),
    categoryId: snowflake.nullable().optional(),
    topic: z.string().trim().max(LIMITS.topic.max).optional(),
    userLimit: z.number().int().min(LIMITS.voiceUserLimit.min).max(LIMITS.voiceUserLimit.max).default(0),
    slowmodeSeconds: z.number().int().min(0).max(21600).default(0),
});
export const updateChannelSchema = z.object({
    name: channelNameSchema.optional(),
    topic: z.string().trim().max(LIMITS.topic.max).nullable().optional(),
    categoryId: snowflake.nullable().optional(),
    position: z.number().int().min(0).optional(),
    userLimit: z.number().int().min(LIMITS.voiceUserLimit.min).max(LIMITS.voiceUserLimit.max).optional(),
    slowmodeSeconds: z.number().int().min(0).max(21600).optional(),
});
export const createCategorySchema = z.object({
    name: z.string().trim().min(LIMITS.categoryName.min).max(LIMITS.categoryName.max),
});
export const reorderChannelsSchema = z.object({
    items: z
        .array(z.object({
        id: snowflake,
        position: z.number().int().min(0),
        categoryId: snowflake.nullable(),
    }))
        .max(200),
});
// ---------------------------------------------------------------------------
// Mensagens
// ---------------------------------------------------------------------------
export const sendMessageSchema = z
    .object({
    content: z.string().max(LIMITS.messageContent.max).default(''),
    replyToId: snowflake.nullable().optional(),
    attachmentIds: z.array(snowflake).max(LIMITS.attachmentsPerMessage).optional(),
    /** Identificador do cliente, ecoado no ACK para casar com a mensagem otimista. */
    nonce: z.string().max(64).optional(),
})
    .refine((v) => v.content.trim().length > 0 || (v.attachmentIds?.length ?? 0) > 0, {
    message: 'A mensagem precisa de texto ou de um anexo',
});
export const editMessageSchema = z.object({
    content: z.string().trim().min(1).max(LIMITS.messageContent.max),
});
export const listMessagesSchema = z.object({
    limit: z.coerce.number().int().min(1).max(LIMITS.maxMessagePageSize).default(LIMITS.messagePageSize),
    before: snowflake.optional(),
    after: snowflake.optional(),
    around: snowflake.optional(),
});
export const reactionSchema = z.object({
    emoji: z.string().min(1).max(32),
});
// ---------------------------------------------------------------------------
// Cargos e permissões
// ---------------------------------------------------------------------------
export const createRoleSchema = z.object({
    name: z.string().trim().min(LIMITS.roleName.min).max(LIMITS.roleName.max),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .optional(),
    permissions: z.string().regex(/^\d+$/).default('0'),
    hoist: z.boolean().default(false),
    mentionable: z.boolean().default(false),
});
export const updateRoleSchema = createRoleSchema.partial().extend({
    position: z.number().int().min(0).optional(),
});
export const updateMemberSchema = z.object({
    nickname: z.string().trim().max(LIMITS.displayName.max).nullable().optional(),
    roleIds: z.array(snowflake).max(64).optional(),
});
export const channelOverwriteSchema = z.object({
    targetId: snowflake,
    targetType: z.enum(['role', 'member']),
    allow: z.string().regex(/^\d+$/),
    deny: z.string().regex(/^\d+$/),
});
// ---------------------------------------------------------------------------
// Convites
// ---------------------------------------------------------------------------
export const createInviteSchema = z.object({
    channelId: snowflake.nullable().optional(),
    maxUses: z.number().int().min(0).max(1000).default(0),
    /** Segundos até expirar. 0 significa nunca. */
    maxAgeSeconds: z.number().int().min(0).max(2592000).default(604800),
});
// ---------------------------------------------------------------------------
// Voz
// ---------------------------------------------------------------------------
export const joinVoiceSchema = z.object({
    channelId: snowflake,
});
export const voiceModerationSchema = z.object({
    userId: snowflake,
    serverMute: z.boolean().optional(),
    serverDeaf: z.boolean().optional(),
    disconnect: z.boolean().optional(),
    moveToChannelId: snowflake.nullable().optional(),
});
// ---------------------------------------------------------------------------
// Administração
// ---------------------------------------------------------------------------
export const adminUserQuerySchema = z.object({
    search: z.string().trim().max(64).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    sort: z.enum(['createdAt', 'lastSeenAt', 'messageCount', 'username']).default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const adminUpdateUserSchema = z.object({
    isAdmin: z.boolean().optional(),
    suspended: z.boolean().optional(),
});
export const metricsRangeSchema = z.object({
    range: z.enum(['1h', '6h', '24h', '7d', '30d']).default('24h'),
});
export const adminSettingsSchema = z.object({
    registrationMode: z.enum(['open', 'invite_only', 'closed']).optional(),
    monthlyEgressQuotaBytes: z.coerce.number().int().min(0).optional(),
    egressAlertPercent: z.coerce.number().int().min(1).max(100).optional(),
    /** Quando a cota passa do alerta, o servidor força o perfil econômico. */
    autoDowngradeQuality: z.boolean().optional(),
    /*
      Os tetos aqui são só sanidade contra erro de digitação, não uma opinião
      sobre qualidade. Quem administra a instalação conhece a própria banda e o
      próprio hardware melhor do que qualquer número fixado no código, então o
      limite fica bem acima de qualquer uso plausível em vez de barrar escolhas
      legítimas.
    */
    maxScreenShareBitrate: z.coerce.number().int().min(100_000).max(50_000_000).optional(),
    maxScreenShareFramerate: z.coerce.number().int().min(5).max(120).optional(),
    maxScreenShareHeight: z.coerce.number().int().min(360).max(2160).optional(),
    maxCameraBitrate: z.coerce.number().int().min(100_000).max(25_000_000).optional(),
});
// ---------------------------------------------------------------------------
// Eventos do gateway
// ---------------------------------------------------------------------------
export const clientEventSchema = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('identify'),
        d: z.object({
            token: z.string().min(1).max(4096),
            client: z.enum(['web', 'desktop']).default('web'),
            clientVersion: z.string().max(32).optional(),
            resume: z
                .object({
                sessionId: z.string().min(1).max(64),
                lastSequence: z.number().int().min(0),
            })
                .optional(),
        }),
    }),
    z.object({
        op: z.literal('heartbeat'),
        d: z.object({
            sequence: z.number().int().min(0),
            /*
              Tempo de ida e volta do heartbeat anterior, medido pelo cliente.
      
              Precisa constar aqui: o schema descarta em silêncio o que não conhece,
              então um campo esquecido não vira erro, vira um valor que simplesmente
              nunca chega do outro lado.
            */
            latencyMs: z.number().min(0).max(60_000).optional(),
        }),
    }),
    z.object({
        op: z.literal('typing'),
        d: z.object({ channelId: snowflake }),
    }),
    z.object({
        op: z.literal('voice_state'),
        d: z.object({
            guildId: snowflake,
            channelId: snowflake.nullable(),
            selfMute: z.boolean(),
            selfDeaf: z.boolean(),
            screenSharing: z.boolean().optional(),
            cameraOn: z.boolean().optional(),
        }),
    }),
    z.object({
        op: z.literal('presence'),
        d: z.object({
            status: z.enum(['online', 'idle', 'dnd', 'offline']),
            customStatus: z.string().max(LIMITS.customStatus.max).nullable().optional(),
        }),
    }),
    z.object({
        op: z.literal('subscribe_guild'),
        d: z.object({ guildId: snowflake }),
    }),
    z.object({
        op: z.literal('ack_read'),
        d: z.object({ channelId: snowflake, messageId: snowflake }),
    }),
]);
//# sourceMappingURL=schemas.js.map